import { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePriceData } from './usePriceData';

interface PriceAlertSettings {
  enabled: boolean;
  lowThreshold: number;
  highThreshold: number;
  notifications: boolean;
  silentMode: boolean;
  alertFrequency: 'low' | 'high' | 'custom';
}

interface PriceAlertState {
  lastAlertTime: number;
  hasPermission: boolean;
  permissionRequested: boolean;
}

const DEFAULT_SETTINGS: PriceAlertSettings = {
  enabled: false,
  lowThreshold: 0.95,
  highThreshold: 1.05,
  notifications: false,
  silentMode: false,
  alertFrequency: 'high',
};

const ALERT_COOLDOWN = 5 * 60 * 1000;
const PRICE_CHECK_INTERVAL = 60 * 1000;

export const usePriceAlert = () => {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const { priceData } = usePriceData();
  const [settings, setSettings] = useState<PriceAlertSettings>(DEFAULT_SETTINGS);
  const [alertState, setAlertState] = useState<PriceAlertState>({
    lastAlertTime: 0,
    hasPermission: false,
    permissionRequested: false,
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 加载设置
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('dor-price-alert-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
      
      const lastAlert = localStorage.getItem('dor-last-alert-time');
      if (lastAlert) {
        setAlertState(prev => ({ ...prev, lastAlertTime: parseInt(lastAlert) }));
      }

      const permissionRequested = localStorage.getItem('dor-notification-permission-requested') === 'true';
      const hasPermission = Notification.permission === 'granted';
      setAlertState(prev => ({ 
        ...prev, 
        permissionRequested,
        hasPermission 
      }));
    } catch (error) {
      // Failed to load price alert settings
    }
  }, []);

  // 保存设置
  const saveSettings = (newSettings: Partial<PriceAlertSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    
    try {
      localStorage.setItem('dor-price-alert-settings', JSON.stringify(updatedSettings));
      
      if (newSettings.enabled && !settings.enabled) {
        toast({
          title: t('priceAlert.enabled'),
          description: t('priceAlert.enabledDescription', { 
            low: updatedSettings.lowThreshold, 
            high: updatedSettings.highThreshold 
          }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      // Failed to save price alert settings
    }
  };

  // 发送浏览器通知
  const sendBrowserNotification = (title: string, body: string, alertType: 'low' | 'high') => {
    if ('Notification' in window && alertState.hasPermission && !settings.silentMode) {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.svg',
        badge: '/favicon.svg',
        tag: 'dor-price-alert',
        requireInteraction: false,
        silent: true,
      });

      notification.onclick = () => {
        window.focus();
        if (alertType === 'low') {
          window.location.href = '/invite';
        } else {
          window.location.href = '/recharge';
        }
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  };

  // 发送Toast通知
  const sendToastNotification = (title: string, description: string, status: 'warning' | 'success' | 'info', actionUrl?: string) => {
    toast({
      title,
      description: actionUrl ? `${description} (点击查看详情)` : description,
      status,
      duration: 8000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // 检查价格并发送提醒
  const checkPriceAndAlert = () => {
    if (!settings.enabled || !priceData?.currentPrice) return;

    const currentPrice = priceData.currentPrice;
    const now = Date.now();
    
    const cooldown = settings.alertFrequency === 'low' ? 15 * 60 * 1000 : ALERT_COOLDOWN;
    
    if (now - alertState.lastAlertTime < cooldown) return;

    let shouldAlert = false;
    let alertType: 'low' | 'high' | null = null;

    if (currentPrice < settings.lowThreshold) {
      shouldAlert = true;
      alertType = 'low';
    } else if (currentPrice > settings.highThreshold) {
      shouldAlert = true;
      alertType = 'high';
    }

    if (shouldAlert && alertType) {
      const deviation = alertType === 'low' 
        ? ((settings.lowThreshold - currentPrice) / settings.lowThreshold * 100).toFixed(1)
        : ((currentPrice - settings.highThreshold) / settings.highThreshold * 100).toFixed(1);

      const title = t('priceAlert.title');
      const body = alertType === 'low'
        ? t('priceAlert.lowBody', { 
            threshold: settings.lowThreshold, 
            price: currentPrice.toFixed(3),
            deviation 
          })
        : t('priceAlert.highBody', { 
            threshold: settings.highThreshold, 
            price: currentPrice.toFixed(3),
            deviation 
          });

      sendBrowserNotification(title, body, alertType);

      const actionUrl = alertType === 'low' ? '/invite' : '/recharge';
      sendToastNotification(
        title,
        body,
        alertType === 'low' ? 'warning' : 'success',
        actionUrl
      );

      setAlertState(prev => ({ ...prev, lastAlertTime: now }));
      localStorage.setItem('dor-last-alert-time', now.toString());
    }
  };

  // 启动价格监控
  useEffect(() => {
    if (settings.enabled) {
      checkPriceAndAlert();
      intervalRef.current = setInterval(checkPriceAndAlert, PRICE_CHECK_INTERVAL);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [settings.enabled, priceData?.currentPrice, alertState.lastAlertTime, settings.lowThreshold, settings.highThreshold]);

  // 请求通知权限
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      const hasPermission = permission === 'granted';
      
      setAlertState(prev => ({ 
        ...prev, 
        hasPermission,
        permissionRequested: true
      }));
      
      localStorage.setItem('dor-notification-permission-requested', 'true');
      
      if (hasPermission) {
        toast({
          title: t('priceAlert.permissionGranted'),
          description: t('priceAlert.permissionGrantedDescription'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('priceAlert.permissionDenied'),
          description: t('priceAlert.permissionDeniedDescription'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      
      return hasPermission;
    }
    return false;
  };

  // 重置权限
  const resetPermission = () => {
    localStorage.removeItem('dor-notification-permission-requested');
    setAlertState(prev => ({ 
      ...prev, 
      permissionRequested: false,
      hasPermission: false
    }));
  };



  return {
    settings,
    alertState,
    saveSettings,
    requestNotificationPermission,
    resetPermission,
    isMonitoring: settings.enabled,
  };
}; 